"use client";

import React from "react";

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* PRICING PAGE – https://www.purposeai.tech/pricing */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8">
            Simple, transparent pricing that scales with your purpose
          </h1>
          <p className="text-2xl md:text-3xl text-gray-700 max-w-4xl mx-auto mb-16">
            One AI coworker. Unlimited everything. No hidden fees. No employees replaced — ever.
          </p>
        </div>
      </section>

      {/* MAIN PRICING TABLE */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Free Tier */}
            <div className="bg-gray-50 rounded-3xl p-10 text-center border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Free</h3>
              <p className="text-6xl font-bold text-gray-900 mb-2">$0<span className="text-2xl font-normal">/mo</span></p>
              <p className="text-gray-600 mb-10">Perfect for testing</p>
              <ul className="text-left space-y-4 mb-10">
                <li>✓ 1 Location</li>
                <li>✓ Website AI Agent only</li>
                <li>✓ Unlimited chat conversations</li>
                <li>✗ No phone AI</li>
                <li>✗ No advanced knowledge base</li>
              </ul>
              <a 
                href="#demo-form" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/#demo-form';
                }}
                className="block w-full bg-gray-300 text-gray-700 py-5 rounded-2xl font-bold text-xl hover:bg-gray-400 transition"
              >
                Start Free
              </a>
            </div>

            {/* Starter – $99 */}
            <div className="bg-gradient-to-b from-teal-600 to-emerald-600 rounded-3xl p-10 text-white relative shadow-2xl transform hover:scale-105 transition">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-6 py-2 rounded-full font-bold">
                MOST POPULAR
              </div>
              <h3 className="text-3xl font-bold mb-4">Starter</h3>
              <p className="text-7xl font-bold mb-2">$99<span className="text-3xl font-normal">/mo</span></p>
              <p className="mb-10 opacity-90">Perfect for single-location practices</p>
              <ul className="text-left space-y-4 mb-10">
                <li>✓ 1 Location</li>
                <li>✓ Website + Phone AI Agent</li>
                <li>✓ Unlimited chat & 25 phone minutes</li>
                <li>✓ Advanced knowledge base</li>
                <li>✓ Zapier, WhatsApp, Google Sheets</li>
                <li>✓ $250 one-time setup</li>
              </ul>
              <a 
                href="#demo-form" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/#demo-form';
                }}
                className="block w-full bg-white text-teal-600 py-5 rounded-2xl font-bold text-xl hover:bg-gray-100 transition shadow-xl"
              >
                Start 14-Day Free Trial
              </a>
            </div>

            {/* Growth – $199 */}
            <div className="bg-white border-4 border-teal-600 rounded-3xl p-10 shadow-2xl relative">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Growth</h3>
              <p className="text-7xl font-bold text-gray-900 mb-2">$199<span className="text-3xl font-normal">/mo</span></p>
              <p className="text-gray-600 mb-10">For growing practices & multi-location</p>
              <ul className="text-left space-y-4 mb-10">
                <li>✓ Up to 5 Locations</li>
                <li>✓ Website + Phone AI Agent</li>
                <li>✓ Unlimited chat & 125 phone minutes</li>
                <li>✓ Advanced knowledge base + training</li>
                <li>✓ All integrations</li>
                <li>✓ $500 one-time setup</li>
              </ul>
              <a 
                href="#demo-form" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/#demo-form';
                }}
                className="block w-full bg-teal-600 text-white py-5 rounded-2xl font-bold text-xl hover:bg-teal-700 transition shadow-xl"
              >
                Start 14-Day Free Trial
              </a>
            </div>

            {/* Pro – $399 */}
            <div className="bg-gradient-to-b from-purple-700 to-indigo-700 rounded-3xl p-10 text-white shadow-2xl transform hover:scale-105 transition relative">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-pink-500 text-white px-6 py-2 rounded-full font-bold">
                BEST VALUE
              </div>
              <h3 className="text-3xl font-bold mb-4">Pro</h3>
              <p className="text-7xl font-bold mb-2">$399<span className="text-3xl font-normal">/mo</span></p>
              <p className="mb-10 opacity-90">For high-volume practices & DSOs</p>
              <ul className="text-left space-y-4 mb-10">
                <li>✓ Up to 10 Locations</li>
                <li>✓ Website + Phone AI Agent</li>
                <li>✓ Unlimited chat & 250 phone minutes</li>
                <li>✓ Full customization & training</li>
                <li>✓ All integrations + priority support</li>
                <li>✓ $750 one-time setup</li>
              </ul>
              <a 
                href="#demo-form" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/#demo-form';
                }}
                className="block w-full bg-white text-purple-700 py-5 rounded-2xl font-bold text-xl hover:bg-gray-100 transition shadow-xl"
              >
                Start 14-Day Free Trial
              </a>
            </div>

            {/* Enterprise – Custom */}
            <div className="bg-gray-900 rounded-3xl p-10 text-white">
              <h3 className="text-3xl font-bold mb-4">Enterprise</h3>
              <p className="text-6xl font-bold mb-2">Custom</p>
              <p className="mb-10 opacity-90">20+ locations • Dedicated support</p>
              <ul className="text-left space-y-4 mb-10">
                <li>✓ Unlimited locations</li>
                <li>✓ Unlimited phone minutes</li>
                <li>✓ Custom AI voice & branding</li>
                <li>✓ White-label option</li>
                <li>✓ SLA & onboarding team</li>
                <li>✓ Custom pricing</li>
              </ul>
              <a 
                href="mailto:jwanek@purposeai.tech" 
                className="block w-full bg-white text-gray-900 py-5 rounded-2xl font-bold text-xl hover:bg-gray-100 transition"
              >
                Contact Sales
              </a>
            </div>
          </div>

          {/* Bottom Trust Bar */}
          <div className="text-center mt-20">
            <p className="text-2xl text-gray-700 mb-8">
              <strong>Every plan includes:</strong> 14-day free trial • HIPAA-compliant • No long-term contracts • Cancel anytime
            </p>
            <a 
              href="#demo-form" 
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/#demo-form';
              }}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-16 py-8 rounded-3xl text-3xl font-bold shadow-2xl transition"
            >
              Start Your Free Trial — No Card Required →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

